import styled from '@emotion/styled/macro'
import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { FieldProps } from 'formik'
import { FormikTextField } from 'formik-office-ui-fabric-react'
import { AuthorizedField as Field } from 'forms/AuthorizedField'
import { FieldStackItem } from 'forms/FormStack'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { SenderRecipient, TransportType } from 'model'
import { DayOfWeek, ITag, Stack } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Section } from 'views/ProvingDetailsView/Layout'
import * as Yup from 'yup'
import { useAddressesQuery } from './SapExportFrom.generated'

export const ProvingTransportDetailsFormSchema = Yup.object().shape({
  transportType: Yup.string().required(),
  outboundTransportSender: Yup.string().when('transportType', {
    is: (val) =>
      val === TransportType.OutboundTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.string().required(),
  }),
  outboundTransportSenderDate: Yup.date().when('transportType', {
    is: (val) =>
      val === TransportType.OutboundTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.date().required(),
    otherwise: Yup.date().nullable(),
  }),
  outboundTransportRecipient: Yup.string().when('transportType', {
    is: (val) =>
      val === TransportType.OutboundTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.string().required(),
  }),
  outboundTransportRecipientDate: Yup.date().when('transportType', {
    is: (val) =>
      val === TransportType.OutboundTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.date().required(),
    otherwise: Yup.date().nullable(),
  }),
  returnTransportSender: Yup.string().when('transportType', {
    is: (val) =>
      val === TransportType.ReturnTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.string().required(),
  }),
  returnTransportSenderDate: Yup.date().when('transportType', {
    is: (val) =>
      val === TransportType.ReturnTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.date().required(),
    otherwise: Yup.date().nullable(),
  }),
  returnTransportRecipient: Yup.string().when('transportType', {
    is: (val) =>
      val === TransportType.ReturnTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.string().required(),
  }),
  returnTransportRecipientDate: Yup.date().when('transportType', {
    is: (val) =>
      val === TransportType.ReturnTransport ||
      val === TransportType.OutboundAndReturnTransport,
    then: Yup.date().required(),
    otherwise: Yup.date().nullable(),
  }),
})

export interface TransportDetailsFormValues {
  transportType: TransportType
  outboundTransportSender: string
  outboundTransportSenderTime: string
  outboundTransportRecipient: string
  outboundTransportRecipientTime: string
  returnTransportSender: string
  returnTransportSenderTime: string
  returnTransportRecipient: string
  returnTransportRecipientTime: string
  outboundTransportSenderContact: string
  outboundTransportRecipientContact: string
  returnTransportSenderContact: string
  returnTransportRecipientContact: string
}

const TimePicker = styled(Field)`
  border-style: solid;
  border-color: rgb(138,  136,  134);
  border-width: 1px;
  border-radius: 2px;
  height: 32px;
  line-height: 30px;
  position: relative;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  background: rgb(255,  255,  255);
  padding: 0px 24px 0px 8px;
  overflow: hidden;
`

export const ProvingTransportDetailsForm: React.FC<{
  annotation?: string
  dateFrom?: string
  dateTo?: string
  disableDeliveryAndPickupDate?: boolean
}> = ({
  annotation,
  dateFrom,
  dateTo,
  disableDeliveryAndPickupDate = false,
}) => {
  const form = useCurrentFormik()
  const [proving] = useCurrentProving()
  const { t } = useTranslation()
  const hasOutboundTransport =
    form.values.transportType &&
    (form.values.transportType === TransportType.OutboundTransport ||
      form.values.transportType === TransportType.OutboundAndReturnTransport)
  const hasReturnTransport =
    form.values.transportType &&
    (form.values.transportType === TransportType.ReturnTransport ||
      form.values.transportType === TransportType.OutboundAndReturnTransport)
  const [selectedOutboundSenderAddress, setSelectedOutboundSenderAddress] =
    React.useState<SenderRecipient | undefined>(
      form.values.outboundTransportSender
    )
  const [selectedReturnSenderAddress, setSelectedReturnSenderAddress] =
    React.useState<SenderRecipient | undefined>(
      form.values.returnTransportSender
    )
  const [
    selectedOutboundRecipientAddress,
    setSelectedOutboundRecipientAddress,
  ] = React.useState<SenderRecipient | undefined>(
    form.values.outboundTransportRecipient
  )
  const [selectedReturnRecipientAddress, setSelectedReturnRecipientAddress] =
    React.useState<SenderRecipient | undefined>(
      form.values.returnTransportRecipient
    )

  const { loading, data, error } = useAddressesQuery()

  const addresses = data && data.senderRecipients ? data.senderRecipients : []

  const addressPickerOptions: ITag[] = addresses
    .map((a) => ({
      key: a._id,
      name: `${a.name}`,
    }))
    .sort((item1, item2) => item1.name.localeCompare(item2.name))

  const renderAddress = (address: SenderRecipient | undefined) =>
    address && (
      <div>
        {address.supplierNumber} <br />
        {address.name} <br />
        {address.street} <br />
        {address.postCode} {address.place}, {address.countryCode}
        <br />
        {address.phone}
        <br />
      </div>
    )

  return (
    <FlexSpinner loading={loading} error={error}>
      <AutoStack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
        {hasOutboundTransport && (
          <Section
            label="Hintransport"
            stack={{ horizontal: true, childrenGap: 16 }}
          >
            <FieldStackItem
              name="outboundTransportSender"
              render={(fieldProps) => (
                <AutoStack tokens={{ childrenGap: 8 }}>
                  <FormikTagPicker
                    {...fieldProps}
                    label={'Beladestelle'}
                    tags={addressPickerOptions}
                    onChange={(item) =>
                      setSelectedOutboundSenderAddress(
                        addresses.find((a) => item.key === a._id)
                      )
                    }
                    onItemsChanged={(items) => {
                      if (!items || items.length === 0) {
                        setSelectedOutboundSenderAddress(undefined)
                      }
                    }}
                    required={true}
                  />
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="outboundTransportSenderDate"
                      render={(props: FieldProps<any>) => {
                        return (
                          <FormikGraphQLDatePicker
                            {...props}
                            isRequired={true}
                            firstDayOfWeek={DayOfWeek.Monday}
                            strings={DayPickerStrings}
                            showWeekNumbers={true}
                            formatDate={onFormatDate as any}
                            initialPickerDate={new Date(proving.dateFrom)}
                          />
                        )
                      }}
                    />
                    <TimePicker
                      component="input"
                      type="time"
                      name="outboundTransportSenderTime"
                    />
                  </Stack>
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="outboundTransportSenderContact"
                      label="Ansprechpartner"
                      component={FormikTextField}
                    />
                  </Stack>
                  {renderAddress(selectedOutboundSenderAddress)}
                </AutoStack>
              )}
            />
            <FieldStackItem
              name="outboundTransportRecipient"
              render={(fieldProps) => (
                <AutoStack tokens={{ childrenGap: 8 }}>
                  <FormikTagPicker
                    {...fieldProps}
                    label={'Entladestelle'}
                    tags={addressPickerOptions}
                    onChange={(item) =>
                      setSelectedOutboundRecipientAddress(
                        addresses.find((a) => item.key === a._id)
                      )
                    }
                    onItemsChanged={(items) => {
                      if (!items || items.length === 0) {
                        setSelectedOutboundRecipientAddress(undefined)
                      }
                    }}
                    required={true}
                  />
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="outboundTransportRecipientDate"
                      render={(props: FieldProps<any>) => {
                        return (
                          <FormikGraphQLDatePicker
                            {...props}
                            disabled={
                              disableDeliveryAndPickupDate ||
                              !fieldProps.form.values
                                .outboundTransportSenderDate
                            }
                            firstDayOfWeek={DayOfWeek.Monday}
                            strings={DayPickerStrings}
                            showWeekNumbers={true}
                            formatDate={onFormatDate as any}
                            isRequired={true}
                            minDate={
                              fieldProps.form.values.outboundTransportSenderDate
                                ? new Date(
                                    fieldProps.form.values.outboundTransportSenderDate
                                  )
                                : undefined
                            }
                            // minDate={new Date(proving.dateFrom)}
                            // maxDate={new Date(proving.dateTo)}
                            initialPickerDate={new Date(proving.dateFrom)}
                          />
                        )
                      }}
                    />
                    <TimePicker
                      component="input"
                      type="time"
                      name="outboundTransportRecipientTime"
                    />
                  </Stack>
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="outboundTransportRecipientContact"
                      label="Ansprechpartner"
                      component={FormikTextField}
                    />
                  </Stack>
                  {renderAddress(selectedOutboundRecipientAddress)}
                  <strong>{annotation}</strong>
                </AutoStack>
              )}
            />
          </Section>
        )}
        {hasReturnTransport && (
          <Section
            label="Rücktransport"
            stack={{ horizontal: true, tokens: { childrenGap: 4 } }}
          >
            <FieldStackItem
              name="returnTransportSender"
              render={(fieldProps) => (
                <AutoStack tokens={{ childrenGap: 8 }}>
                  <FormikTagPicker
                    {...fieldProps}
                    label={'Beladestelle'}
                    tags={addressPickerOptions}
                    onChange={(item) =>
                      setSelectedReturnSenderAddress(
                        addresses.find((a) => item.key === a._id)
                      )
                    }
                    onItemsChanged={(items) => {
                      if (!items || items.length === 0) {
                        setSelectedReturnSenderAddress(undefined)
                      }
                    }}
                    required={true}
                  />
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="returnTransportSenderDate"
                      render={(props: FieldProps<any>) => {
                        return (
                          <FormikGraphQLDatePicker
                            {...props}
                            disabled={disableDeliveryAndPickupDate}
                            firstDayOfWeek={DayOfWeek.Monday}
                            strings={DayPickerStrings}
                            showWeekNumbers={true}
                            formatDate={onFormatDate as any}
                            isRequired={true}
                            // minDate={new Date(proving.dateFrom)}
                            // maxDate={new Date(proving.dateTo)}
                            initialPickerDate={new Date(proving.dateTo)}
                          />
                        )
                      }}
                    />
                    <TimePicker
                      component="input"
                      type="time"
                      name="returnTransportSenderTime"
                    />
                  </Stack>
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="returnTransportSenderContact"
                      label="Ansprechpartner"
                      component={FormikTextField}
                    />
                  </Stack>
                  {renderAddress(selectedReturnSenderAddress)}
                </AutoStack>
              )}
            />
            <FieldStackItem
              name="returnTransportRecipient"
              render={(fieldProps) => (
                <AutoStack tokens={{ childrenGap: 8 }}>
                  <FormikTagPicker
                    {...fieldProps}
                    label={'Entladestelle'}
                    tags={addressPickerOptions}
                    onChange={(item) =>
                      setSelectedReturnRecipientAddress(
                        addresses.find((a) => item.key === a._id)
                      )
                    }
                    onItemsChanged={(items) => {
                      if (!items || items.length === 0) {
                        setSelectedReturnRecipientAddress(undefined)
                      }
                    }}
                    required={true}
                  />
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="returnTransportRecipientDate"
                      render={(props: FieldProps<any>) => {
                        return (
                          <FormikGraphQLDatePicker
                            {...props}
                            firstDayOfWeek={DayOfWeek.Monday}
                            strings={DayPickerStrings}
                            showWeekNumbers={true}
                            formatDate={onFormatDate as any}
                            isRequired={true}
                            minDate={
                              fieldProps.form.values.returnTransportSenderDate
                                ? new Date(
                                    fieldProps.form.values.returnTransportSenderDate
                                  )
                                : undefined
                            }
                            disabled={
                              !fieldProps.form.values.returnTransportSenderDate
                            }
                            initialPickerDate={
                              dateFrom ? new Date(dateFrom) : undefined
                            }
                          />
                        )
                      }}
                    />
                    <TimePicker
                      component="input"
                      type="time"
                      name="returnTransportRecipientTime"
                    />
                  </Stack>
                  <Stack horizontal={true} tokens={{ childrenGap: 16 }}>
                    <Field
                      name="returnTransportRecipientContact"
                      label="Ansprechpartner"
                      component={FormikTextField}
                    />
                  </Stack>
                  {renderAddress(selectedReturnRecipientAddress)}
                </AutoStack>
              )}
            />
          </Section>
        )}
      </AutoStack>
    </FlexSpinner>
  )
}
